import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import type { FeatureKey, PublicPlan } from '../lib/types';
import { FEATURE_LABELS, fmtMoney, fmtNum } from './adminShared';

/**
 * "Mejorar plan" (RFC-0027). Mientras no haya cobro en línea, elegir un
 * plan manda la solicitud al dueño con el contexto de la organización y
 * él lo activa desde la consola. Los planes y sus precios salen del API
 * (editables en la consola), no del código.
 */
export function UpgradePlans({ currentPlan, email }: { currentPlan: string; email: string }) {
  const [plans, setPlans] = useState<PublicPlan[] | null>(null);
  const [choice, setChoice] = useState<string | null>(null);
  const [message, setMessage] = useState('');
  const [state, setState] = useState<'idle' | 'sending' | 'sent' | 'error' | 'limited'>('idle');

  useEffect(() => {
    let alive = true;
    api.plans()
      .then((r) => { if (alive) setPlans(r.plans.filter((p) => p.code !== 'TRIAL')); })
      .catch(() => { if (alive) setPlans([]); });
    return () => { alive = false; };
  }, []);

  async function send(e: React.FormEvent) {
    e.preventDefault();
    if (state === 'sending') return;
    setState('sending');
    try {
      await api.accountUpgradeRequest(choice, message.trim() || null);
      setState('sent');
    } catch (err) {
      setState((err as { status?: number } | null)?.status === 429 ? 'limited' : 'error');
    }
  }

  if (state === 'sent') {
    return (
      <div className="auth-ok">
        Listo: le avisamos a TerraKairos{choice ? ` que quieres el plan ${plans?.find((p) => p.code === choice)?.name ?? choice}` : ''}.
        Te contactarán en <strong>{email}</strong> para activarlo.
      </div>
    );
  }

  return (
    <form className="upgrade" onSubmit={send}>
      <div className="section-title" style={{ marginTop: 4 }}>Mejorar plan</div>
      {plans === null ? (
        <p className="auth-hint">Cargando planes…</p>
      ) : plans.length > 0 && (
        <div className="upgrade-grid" role="radiogroup" aria-label="Planes">
          {plans.map((p) => {
            const current = p.code === currentPlan;
            const selected = choice === p.code;
            const views = p.limits.property_views_per_month;
            return (
              <button
                type="button"
                key={p.code}
                role="radio"
                aria-checked={selected}
                disabled={current}
                className={`upgrade-card${selected ? ' selected' : ''}${current ? ' current' : ''}`}
                onClick={() => setChoice(selected ? null : p.code)}
              >
                <span className="upgrade-name">{p.name}{current && <span className="pill">tu plan</span>}</span>
                <span className="upgrade-price">
                  {p.priceMonthlyMxn !== null ? <>{fmtMoney(p.priceMonthlyMxn)} <small>MXN/mes</small></> : 'Precio a convenir'}
                </span>
                <span className="upgrade-line">{fmtNum(p.defaultSeats)} usuarios · {views === null || views === undefined ? 'fichas ilimitadas' : `${fmtNum(views)} fichas/mes`}</span>
                <ul className="upgrade-features">
                  {(Object.keys(FEATURE_LABELS) as FeatureKey[]).filter((k) => p.features[k]).map((k) => (
                    <li key={k}>✓ {FEATURE_LABELS[k]}</li>
                  ))}
                </ul>
              </button>
            );
          })}
        </div>
      )}
      <label className="upgrade-msg">
        <span>¿Algo que debamos saber? <span className="label-optional">(opcional: cuántos usuarios, qué zonas, si necesitas exportar o API)</span></span>
        <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={2} maxLength={1000} />
      </label>
      {state === 'error' && <div className="auth-error">No pudimos enviar la solicitud. Intenta de nuevo.</div>}
      {state === 'limited' && <div className="auth-error">Ya recibimos tus solicitudes de hoy; te contactaremos pronto.</div>}
      <button type="submit" className="auth-submit" disabled={state === 'sending'}>
        {state === 'sending' ? 'Enviando…' : choice ? `Solicitar el plan ${plans?.find((p) => p.code === choice)?.name ?? ''}` : 'Hablar con TerraKairos'}
      </button>
    </form>
  );
}
